import { t } from "@lingui/macro";
import { type CallerMemory, normalizeCallerMemory } from "@repo/common";
import { db, functions, httpsCallable } from "@repo/ui";
import { getAuth } from "firebase/auth";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { parsePhoneNumber } from "libphonenumber-js";
import { Loader2 } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { i18n } from "../i18n";
import { getContentFieldProps } from "../utils/textDirection";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "./ui/alert-dialog";
import { Button } from "./ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./ui/select";
import { Textarea } from "./ui/textarea";

// We safely mirror the backend normalization logic for the frontend
function canonicalNormalizePhoneNumber(phone: string): string | null {
	if (!phone) return null;
	const trimmed = phone.trim();
	if (trimmed === "") return null;

	try {
		const parsed = parsePhoneNumber(trimmed);
		if (parsed.isValid()) {
			return parsed.format("E.164");
		}
	} catch {
		// fallback to null
	}
	return null;
}

interface CallerMemoryCardProps {
	companyId: string;
	phoneNumber: string;
	language: "en" | "he";
	isOutbound?: boolean;
}

export function CallerMemoryCard({
	companyId,
	phoneNumber,
	language,
	isOutbound = false,
}: CallerMemoryCardProps) {
	const [memory, setMemory] = useState<CallerMemory | null>(null);
	const [isEnabled, setIsEnabled] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(true);

	useEffect(() => {
		let unsubscribe: (() => void) | undefined;

		async function checkFlagsAndSubscribe() {
			try {
				// 1. Check feature flags
				const sysSnap = await getDoc(doc(db, "settings", "system"));
				const sysSettings = sysSnap.exists() ? sysSnap.data() : {};
				const memorySettings = sysSettings?.callerMemory || {};
				const globallyDisabled = memorySettings.disabledForAll === true;
				const defaultEnabled = memorySettings.userUiEnabled === true;

				const companySnap = await getDoc(doc(db, "companies", companyId));
				const companyData = companySnap.exists() ? companySnap.data() : {};
				const companyOverride = companyData?.features?.callerMemoryUserUi;

				const effectiveEnabled = globallyDisabled
					? false
					: (companyOverride ?? defaultEnabled);

				setIsEnabled(effectiveEnabled);

				if (!effectiveEnabled) {
					setLoading(false);
					return;
				}

				// 2. Subscribe to caller memory snapshot
				const normalizedId = canonicalNormalizePhoneNumber(phoneNumber);
				if (!normalizedId) {
					setLoading(false);
					return;
				}

				const callerRef = doc(
					db,
					"companies",
					companyId,
					"callers",
					normalizedId,
				);
				unsubscribe = onSnapshot(
					callerRef,
					(snap) => {
						if (snap.exists()) {
							const data = snap.data();
							setMemory(normalizeCallerMemory(data.memory) || null);
						} else {
							setMemory(null);
						}
						setLoading(false);
					},
					(err) => {
						console.error("Error fetching caller memory:", err);
						setLoading(false);
					},
				);
			} catch (err) {
				console.error("Error checking caller memory feature flags:", err);
				setLoading(false);
			}
		}

		checkFlagsAndSubscribe();

		return () => {
			if (unsubscribe) unsubscribe();
		};
	}, [companyId, phoneNumber]);

	const [canEdit, setCanEdit] = useState<boolean>(false);
	const [isAddOpen, setIsAddOpen] = useState(false);
	const [isClearOpen, setIsClearOpen] = useState(false);
	const [submitting, setSubmitting] = useState(false);

	const [category, setCategory] = useState("fact");
	const [text, setText] = useState("");
	const [reference, setReference] = useState("");
	const textDirection = language === "he" ? "rtl" : "ltr";
	const submissionKeyRef = useRef<string | null>(null);

	const updateDraft = <T,>(
		setter: React.Dispatch<React.SetStateAction<T>>,
		value: T,
	) => {
		submissionKeyRef.current = null;
		setter(value);
	};

	useEffect(() => {
		const auth = getAuth();
		auth.currentUser
			?.getIdTokenResult()
			.then((result) => {
				const claims = result.claims;
				const systemAdmin = claims.systemAdmin === true;
				const companyAdmin = (claims.companyAdmin as string[]) || [];
				setCanEdit(systemAdmin || companyAdmin.includes(companyId));
			})
			.catch((e) => console.error("Failed to fetch claims:", e));
	}, [companyId]);

	// Fast-fail if the feature flag is disabled
	if (!isEnabled) return null;

	const handleAddMemory = async () => {
		if (!text.trim()) {
			toast.error(
				t({ id: "updateTextRequired", message: "Update text is required." }),
			);
			return;
		}

		setSubmitting(true);
		try {
			const idempotencyKey = submissionKeyRef.current ?? crypto.randomUUID();
			submissionKeyRef.current = idempotencyKey;
			const submitCallable = httpsCallable(
				functions,
				"submitCallerMemoryUpdate",
			);
			const payload: Record<string, unknown> = {
				companyId,
				callerId: phoneNumber,
				category,
				submittedText: text,
				idempotencyKey,
			};
			const trimmedReference = reference.trim();
			if (trimmedReference) payload.reference = trimmedReference;
			await submitCallable(payload);
			toast.success(
				t({
					id: "callerDetailUpdateSuccess",
					message:
						"Caller detail submitted. It will be processed and shown here after the memory update completes.",
				}),
			);
			setIsAddOpen(false);
			setText("");
			setReference("");
			submissionKeyRef.current = null;
		} catch (err) {
			const message = err instanceof Error ? err.message : String(err);
			toast.error(
				i18n._({
					id: "callerDetailUpdateFailed",
					message: "Failed to update caller details: {error}",
					values: { error: message },
				}),
			);
		} finally {
			setSubmitting(false);
		}
	};

	const handleClearMemory = async () => {
		setSubmitting(true);
		try {
			const clearCallable = httpsCallable(functions, "clearCallerMemory");
			await clearCallable({ companyId, callerId: phoneNumber });
			toast.success(
				t({
					id: "callerDetailsClearedSuccess",
					message: "Saved caller details cleared successfully.",
				}),
			);
			setIsClearOpen(false);
		} catch (err) {
			const message = err instanceof Error ? err.message : String(err);
			toast.error(
				i18n._({
					id: "callerDetailsClearFailed",
					message: "Failed to clear saved caller details: {error}",
					values: { error: message },
				}),
			);
		} finally {
			setSubmitting(false);
		}
	};

	const hasMemory =
		memory &&
		Boolean(
			memory?.summary ||
				(memory?.facts && memory.facts.length > 0) ||
				(memory?.preferences && memory.preferences.length > 0),
		);

	return (
		<>
			{loading ? (
				<div className="flex justify-center border-t pt-4">
					<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
				</div>
			) : !hasMemory ? (
				canEdit && (
					<div className="flex justify-end border-t pt-4">
						<Button
							variant="outline"
							size="sm"
							onClick={() => setIsAddOpen(true)}
							className="h-7 text-xs"
						>
							+ {t({ id: "addCallerDetail", message: "Add detail" })}
						</Button>
					</div>
				)
			) : (
				<div className="space-y-4 border-t pt-4">
					{memory.summary && (
						<div>
							<h4 className="mb-1 text-xs font-medium text-muted-foreground">
								{t({ id: "callerSummary", message: "Summary" })}
							</h4>
							<div className="whitespace-pre-wrap text-sm text-foreground">
								{memory.summary}
							</div>
						</div>
					)}

					{(memory.preferences?.length > 0 || memory.facts?.length > 0) && (
						<div className="space-y-3">
							{memory.preferences && memory.preferences.length > 0 && (
								<div>
									<h4 className="mb-1 text-xs font-medium text-muted-foreground">
										{t({ id: "preferences", message: "Preferences" })}
									</h4>
									<ul className="list-disc list-inside text-sm text-muted-foreground space-y-0.5 ml-1">
										{memory.preferences.map((pref: string, i: number) => (
											<li key={i}>{pref}</li>
										))}
									</ul>
								</div>
							)}

							{memory.facts && memory.facts.length > 0 && (
								<div>
									<h4 className="mb-1 text-xs font-medium text-muted-foreground">
										{t({ id: "knownFacts", message: "Known Facts" })}
									</h4>
									<ul className="list-disc list-inside text-sm text-muted-foreground space-y-0.5 ml-1">
										{memory.facts.map((fact: string, i: number) => (
											<li key={i}>{fact}</li>
										))}
									</ul>
								</div>
							)}
						</div>
					)}

					{canEdit && (
						<div className="flex justify-end gap-2 pt-1">
							<Button
								variant="outline"
								size="sm"
								onClick={() => setIsAddOpen(true)}
								className="h-7 text-xs"
							>
								+ {t({ id: "addCallerDetail", message: "Add detail" })}
							</Button>
							<Button
								variant="outline"
								size="sm"
								onClick={() => setIsClearOpen(true)}
								className="h-7 text-xs text-danger-ink hover:text-danger-ink"
							>
								{t({
									id: "clearSavedCallerDetails",
									message: "Clear saved details",
								})}
							</Button>
						</div>
					)}
				</div>
			)}

			{/* Add caller detail dialog */}
			<Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>
							{isOutbound
								? t({
										id: "addCalleeDetailTitle",
										message: "Add Callee Detail",
									})
								: t({
										id: "addCallerDetailTitle",
										message: "Add Caller Detail",
									})}
						</DialogTitle>
						<DialogDescription>
							{isOutbound
								? t({
										id: "addCalleeDetailDesc",
										message:
											"Add a fact or preference for this callee. Updates are processed asynchronously and may not appear immediately.",
									})
								: t({
										id: "addCallerDetailDesc",
										message:
											"Add a fact or preference for this caller. Updates are processed asynchronously and may not appear immediately.",
									})}
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-4 py-4">
						<div className="space-y-2">
							<Label>{t({ id: "category", message: "Category" })}</Label>
							<Select
								value={category}
								onValueChange={(value) => updateDraft(setCategory, value)}
							>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="fact">
										{t({ id: "fact", message: "Fact" })}
									</SelectItem>
									<SelectItem value="preference">
										{t({ id: "preference", message: "Preference" })}
									</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-2">
							<Label>{t({ id: "updateText", message: "Update Text" })}</Label>
							<Textarea
								value={text}
								onChange={(e) => updateDraft(setText, e.target.value)}
								placeholder={
									isOutbound
										? t({
												id: "addCalleeDetailPlaceholder",
												message:
													"E.g. Callee prefers to be called in the evening",
											})
										: t({
												id: "updateTextPlaceholder",
												message:
													"E.g. Caller prefers to be called in the evening",
											})
								}
								maxLength={500}
								{...getContentFieldProps(text, textDirection)}
							/>
						</div>
						<div className="space-y-2">
							<Label>
								{t({ id: "reference", message: "Reference (Optional)" })}
							</Label>
							<Input
								value={reference}
								onChange={(e) => updateDraft(setReference, e.target.value)}
								placeholder={t({
									id: "referencePlaceholder",
									message: "Context or ticket ID",
								})}
								maxLength={200}
								{...getContentFieldProps(reference, textDirection)}
							/>
						</div>
					</div>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setIsAddOpen(false)}
							disabled={submitting}
						>
							{t({ id: "cancel", message: "Cancel" })}
						</Button>
						<Button onClick={handleAddMemory} disabled={submitting}>
							{submitting
								? t({ id: "submitting", message: "Submitting..." })
								: t({ id: "submit", message: "Submit" })}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Clear saved caller details alert */}
			{hasMemory && (
				<AlertDialog open={isClearOpen} onOpenChange={setIsClearOpen}>
					<AlertDialogContent>
						<AlertDialogHeader>
							<AlertDialogTitle>
								{isOutbound
									? t({
											id: "clearCalleeDetailsTitle",
											message: "Clear saved callee details?",
										})
									: t({
											id: "clearCallerDetailsTitle",
											message: "Clear saved caller details?",
										})}
							</AlertDialogTitle>
							<AlertDialogDescription>
								{isOutbound
									? t({
											id: "clearCalleeDetailsDesc",
											message:
												"This action cannot be undone. It will remove the saved summary, facts, and preferences for this callee.",
										})
									: t({
											id: "clearCallerDetailsDesc",
											message:
												"This action cannot be undone. It will remove the saved summary, facts, and preferences for this caller.",
										})}
							</AlertDialogDescription>
						</AlertDialogHeader>
						<AlertDialogFooter>
							<AlertDialogCancel disabled={submitting}>
								{t({ id: "cancel", message: "Cancel" })}
							</AlertDialogCancel>
							<AlertDialogAction
								onClick={(e) => {
									e.preventDefault();
									handleClearMemory();
								}}
								disabled={submitting}
								className="bg-danger-tint text-danger-ink hover:bg-danger-tint/80"
							>
								{submitting
									? t({ id: "clearing", message: "Clearing..." })
									: t({
											id: "clearCallerDetailsAction",
											message: "Yes, clear details",
										})}
							</AlertDialogAction>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>
			)}
		</>
	);
}
